import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TrainerLayout from '../../components/trainer/TrainerLayout';
import { Search, Mail, MessageSquare, Download, GraduationCap, Clock, CheckCircle } from 'lucide-react';

const mockStudents = [
  { id: 1, name: 'Rahul Mehta', email: 'rahul@example.com', course: 'AI for Finance', progress: 65, status: 'active', lastActive: '2 min ago', assignments: 4, totalAssignments: 6 },
  { id: 2, name: 'Ananya Krishnan', email: 'ananya@example.com', course: 'AI for Finance', progress: 90, status: 'active', lastActive: '1 hour ago', assignments: 6, totalAssignments: 6 },
  { id: 3, name: 'Vikram Joshi', email: 'vikram@example.com', course: 'Data Science for Banking', progress: 30, status: 'active', lastActive: '3 hours ago', assignments: 2, totalAssignments: 4 },
  { id: 4, name: 'Priya Nair', email: 'priya@example.com', course: 'AI for Finance', progress: 10, status: 'at_risk', lastActive: '1 week ago', assignments: 1, totalAssignments: 6 },
  { id: 5, name: 'Arjun Shah', email: 'arjun@example.com', course: 'Credit Risk Modeling', progress: 100, status: 'completed', lastActive: '2 days ago', assignments: 5, totalAssignments: 5 },
];

export default function TrainerStudents(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');

  const filtered = mockStudents.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase());
    const matchesCourse = filterCourse === 'all' || s.course === filterCourse;
    return matchesSearch && matchesCourse;
  });

  return (
    <TrainerLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Students</h1>
            <p className="text-sm text-slate-500">Track student progress and engagement</p>
          </div>
          <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
            />
          </div>
          <select
            value={filterCourse}
            onChange={e => setFilterCourse(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
          >
            <option value="all">All Courses</option>
            <option value="AI for Finance">AI for Finance</option>
            <option value="Data Science for Banking">Data Science for Banking</option>
            <option value="Credit Risk Modeling">Credit Risk Modeling</option>
          </select>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Student</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Course</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Progress</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Assignments</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-xs">
                          {s.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                          <p className="text-xs text-slate-500">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{s.course}</td>
                    <td className="px-5 py-4">
                      <div className="w-24">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-500">{s.progress}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${s.progress >= 80 ? 'bg-green-500' : s.progress >= 40 ? 'bg-amber-500' : 'bg-amber-500'}`} style={{ width: `${s.progress}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{s.assignments}/{s.totalAssignments}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        s.status === 'completed' ? 'bg-green-50 text-green-700' :
                        s.status === 'at_risk' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-600'
                      }`}>
                        {s.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1">
                        <a href={`mailto:${s.email}`} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors" title="Email">
                          <Mail className="w-4 h-4" />
                        </a>
                        <button className="p-1.5 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors" title="WhatsApp">
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </TrainerLayout>
  );
}
